import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import classNames from "classnames";
import toast from "react-hot-toast";
import { IconCode, IconCopy, IconInfo, IconRefresh } from "../../utils/icons";
import { formatMetaTags, highlightJson } from "../../utils/jsonLdDisplay";
import { eligibilitySummary, JsonLdEligibility, JsonLdValidation } from "../JsonLdEligibility/JsonLdEligibility";

// The generated-markup block for ONE PDP URL row: eligibility line, the
// Video Object / Page markup / Meta tags switch, Copy, and (when the
// caller can regenerate) Generate / Regenerate. Shared by the product
// settings panel and the video settings panel so the two Product Pages tabs
// read and behave the same. A page carries any number of our videos, one
// link each (`videos`, TDC-1644), and each link has its own VideoObject;
// the Video Object tab publishes them all (plus the retailer's other
// videos) as one @graph. `ownFileId` says which link the eligibility line
// and the Generate label speak for — the video whose settings we are in;
// the product panel, with no video in context, gets the first link. Two
// generators: the VideoObject needs a video (its transcript is the
// source), so a caller passes `onGenerate` only when the row has one; the
// Page markup (the one block gt.js injects: every video on the page as a
// VideoObject, never a Product node of ours — the page has its own) is
// rebuilt by `onGenerateProduct` from what the last analysis found.

export type JsonLdOutputTab = "videoJsonld" | "productJsonld" | "metadata";

export const JSON_LD_TAB_LABELS: Record<JsonLdOutputTab, string> = {
    videoJsonld: "Video Object JSON-LD",
    productJsonld: "Page JSON-LD",
    metadata: "Meta tags"
};

// One of our videos on the page (API: lib/pdpUrlVideos): the file URL the
// retailer serves for it, the Google overrides, and its VideoObject with
// Google's check and what the overrides form prefills.
export interface PdpUrlVideoLink {
    id: number;
    s3_file_id: number;
    video_file_url: string | null;
    thumbnail_url_override: string | null;
    upload_date_override: string | null;
    json_ld?: any;
    json_ld_validation?: JsonLdValidation | null;
    json_ld_defaults?: { thumbnailUrl: string | null; uploadDate: string | null } | null;
    videoFile?: {
        id: number;
        name: string | null;
        key: string | null;
        location?: string | null;
        cloudFrontDomain?: string | null;
    } | null;
}

// A video the audit scrape found on the retailer page, with the VideoObject
// generated for it (API: lib/pageVideos). Ours are the ones detection
// matched to a link (stamped with that file's id); the rest are the
// retailer's other videos.
export interface PageVideo {
    kind: "jsonld" | "video_tag" | "inline" | "attribute" | "iframe";
    // Set by the API when detection matched this page video to one of the
    // page's linked videos (known file URL, or duration + aspect ratio).
    ours?: boolean;
    s3_file_id?: number | null;
    contentUrl?: string | null;
    embedUrl?: string | null;
    poster?: string | null;
    duration?: number | null;
    probed_at?: string | null;
    // 'tag' = only the google tag reported it from a visitor's browser; no
    // page check has seen it yet (it expires after 14 days unseen).
    source?: "tag" | string | null;
    reported_at?: string | null;
    json_ld?: any;
    json_ld_validation?: JsonLdValidation | null;
}

// A page video only the tag reported: shown muted until a page check
// confirms it; never counted as found.
export const tagOnlyVideo = (v: PageVideo) => v.source === "tag";

export interface JsonLdRow {
    videos?: PdpUrlVideoLink[] | null;
    product_json_ld?: any;
    product_json_ld_validation?: JsonLdValidation | null;
    page_videos?: PageVideo[] | null;
}

const sameVideoUrl = (a?: string | null, b?: string | null) => {
    const norm = (u?: string | null) => String(u || "").trim().toLowerCase().replace(/^https?:/, "").replace(/^\/\//, "");
    const na = norm(a);
    return !!na && na === norm(b);
};

// The link a page video belongs to: the stamp detection wrote, else the
// link whose file URL is this file. Null for the retailer's own videos.
export const linkForPageVideo = (row: JsonLdRow, v: PageVideo): PdpUrlVideoLink | null => {
    const links = row.videos || [];
    if (v.s3_file_id) {
        const stamped = links.find((l) => Number(l.s3_file_id) === Number(v.s3_file_id));
        if (stamped) return stamped;
    }
    return links.find((l) => sameVideoUrl(v.contentUrl, l.video_file_url)) || null;
};

// The link the block speaks for: the video in context, else the page's
// first video.
export const ownLinkFor = (row: JsonLdRow, ownFileId?: number | null): PdpUrlVideoLink | null => {
    const links = row.videos || [];
    if (ownFileId != null) return links.find((l) => Number(l.s3_file_id) === Number(ownFileId)) || null;
    return links[0] || null;
};

// The first VideoObject of a stored page markup (plain node or @graph).
const firstVideoNode = (blob: any): any => {
    if (!blob || typeof blob !== "object") return null;
    if (Array.isArray(blob["@graph"])) return blob["@graph"][0] || null;
    return blob;
};

// What goes on the page for the Video Object tab: every page video's
// markup, plus each of our videos' markup when its file is not among them,
// as one @graph (a single VideoObject stays a plain object, the shape
// brands already inject).
export const videoObjectsFor = (row: JsonLdRow): any[] => {
    const pageVideos = row.page_videos || [];
    const page = pageVideos.map((v) => v.json_ld).filter(Boolean);
    const onPage = new Set(
        pageVideos.map((v) => linkForPageVideo(row, v)).filter(Boolean).map((l) => (l as PdpUrlVideoLink).s3_file_id)
    );
    const offPage = (row.videos || []).filter((l) => l.json_ld && !onPage.has(l.s3_file_id)).map((l) => l.json_ld);
    return [...offPage, ...page];
};

const videoGraph = (objects: any[]) => {
    if (objects.length <= 1) return objects[0] || null;
    return {
        "@context": "https://schema.org",
        "@graph": objects.map((o) => {
            const node = { ...o };
            delete node["@context"];
            return node;
        })
    };
};

const KIND_LABELS: Record<string, string> = {
    iframe: "player",
    jsonld: "retailer markup",
    video_tag: "video tag",
    inline: "page script",
    attribute: "page markup"
};

const fmtDuration = (seconds?: number | null) => {
    if (!seconds || seconds <= 0) return null;
    const m = Math.floor(Math.round(seconds) / 60);
    const s = Math.round(seconds) % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

interface Props {
    row: JsonLdRow;
    // The video in context (its settings modal) — whose link the eligibility
    // line, the "ours" labels and the Generate label refer to. Omitted (the
    // product panel), the page's first video stands in.
    ownFileId?: number | null;
    // Regenerate from the linked video's transcript. Omit when the row has no
    // video — the button hides and the empty state says why.
    onGenerate?: () => void;
    generating?: boolean;
    // Rebuild the page markup from the videos the last analysis found.
    onGenerateProduct?: () => void;
    generatingProduct?: boolean;
    // "sm" inline in a stacked list (product panel); "lg" as a section
    // heading (video panel).
    eligibilitySize?: "sm" | "lg";
    // What the empty state says when nothing can be generated here.
    noVideoHint?: string;
    className?: string;
}

const PRE_CLS =
    "bg-surface-2 border border-border-token rounded-[11px] px-[18px] py-4 font-mono text-[12.5px] leading-[1.65] text-text-2 overflow-auto max-h-72 transition-opacity";

// What goes on the retailer's page: the blob inside its script tag. Shown
// and copied in this exact form so the pane is what you paste.
const asScriptTag = (blob: any) =>
    `<script type="application/ld+json">\n${JSON.stringify(blob, null, 2)}\n</script>`;
const SCRIPT_OPEN = '&lt;script type="application/ld+json"&gt;';
const SCRIPT_CLOSE = "&lt;/script&gt;";

export function PdpJsonLdOutput({
    row,
    ownFileId,
    onGenerate,
    generating,
    onGenerateProduct,
    generatingProduct,
    eligibilitySize = "sm",
    noVideoHint = "Assign a video to this page to generate it.",
    className
}: Props) {
    const [tab, setTab] = useState<JsonLdOutputTab>("videoJsonld");

    const ownLink = ownLinkFor(row, ownFileId);
    const ownJsonLd = ownLink?.json_ld || null;
    const ownValidation = ownLink?.json_ld_validation || null;
    const links = row.videos || [];

    // Meta tags come from the VideoObject (name / description / keywords);
    // without a linked video, the page markup's first video stands in.
    const metaTags = useMemo(
        () => formatMetaTags(ownJsonLd || firstVideoNode(row.product_json_ld)),
        [ownJsonLd, row.product_json_ld]
    );
    const videoBlob = useMemo(() => videoGraph(videoObjectsFor(row)), [row.videos, row.page_videos]);
    const highlightedVideo = useMemo(
        () =>
            videoBlob
                ? `${SCRIPT_OPEN}\n${highlightJson(JSON.stringify(videoBlob, null, 2))}\n${SCRIPT_CLOSE}`
                : "",
        [videoBlob]
    );
    const highlightedProduct = useMemo(
        () =>
            row.product_json_ld
                ? `${SCRIPT_OPEN}\n${highlightJson(JSON.stringify(row.product_json_ld, null, 2))}\n${SCRIPT_CLOSE}`
                : "",
        [row.product_json_ld]
    );

    const hasContent: Record<JsonLdOutputTab, boolean> = {
        videoJsonld: !!videoBlob,
        productJsonld: !!row.product_json_ld,
        metadata: !!metaTags
    };

    const handleCopy = () => {
        const blob = tab === "productJsonld" ? row.product_json_ld : videoBlob;
        let text = "";
        if (tab === "metadata") text = metaTags;
        else if (blob) text = asScriptTag(blob);
        if (!text) return;
        // Copying is when this markup goes into a retailer's CMS — hold it
        // back while Google would reject it.
        const validation = tab === "productJsonld" ? row.product_json_ld_validation : ownValidation;
        if (validation && !validation.eligible) {
            toast.error(eligibilitySummary(validation));
            return;
        }
        navigator.clipboard?.writeText(text);
        toast.success(`${JSON_LD_TAB_LABELS[tab]} copied to clipboard!`);
    };

    const tabCls = (on: boolean) =>
        classNames(
            "inline-flex items-center gap-[6px] px-[14px] py-[7px] rounded-[7px] text-[13px] font-semibold transition-colors",
            on ? "bg-surface text-text-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "text-muted hover:text-text-2"
        );

    // Generation reads what the last PDP analysis found on the page (its
    // videos, their file URLs, posters and durations). Until one has run the
    // row has no page_videos at all (null, not an empty list), so the
    // markup would describe nothing real — hold the button.
    const analyzed = row.page_videos !== null && row.page_videos !== undefined;
    // Which generator the active tab drives. The Video Object tab needs one
    // of our videos for the transcript-based markup; without one it still
    // rebuilds the VideoObjects for whatever the analysis found on the
    // page, which the product generator does.
    const isProductTab = tab === "productJsonld";
    const pageVideoCount = (row.page_videos || []).length;
    const videoAction = onGenerate || (pageVideoCount > 0 ? onGenerateProduct : undefined);
    const generateAction = isProductTab ? onGenerateProduct : videoAction;
    const busy = isProductTab ? !!generatingProduct : !!(generating || (!onGenerate && generatingProduct));
    let generateTitle = "Regenerate this page's JSON-LD from the linked video's transcript";
    if (!analyzed) generateTitle = "Run a PDP Analysis first — generation uses the videos it finds on the page";
    else if (isProductTab) generateTitle = "Rebuild the page markup from the videos found on this page";
    else if (!onGenerate) generateTitle = "Rebuild the VideoObjects for the videos found on this page";
    let generateLabel = "Generate";
    if (busy) generateLabel = "Generating…";
    else if (isProductTab ? row.product_json_ld : ownJsonLd) generateLabel = "Regenerate";

    let content: React.ReactNode;
    if (tab === "metadata") {
        content = metaTags ? (
            <pre className={classNames(PRE_CLS, "whitespace-pre-wrap break-words")}>{metaTags}</pre>
        ) : null;
        if (!content) {
            content = (
                <p className="text-[13px] text-muted leading-[1.5]">
                    No meta tags yet. Generate the Video Object (or the page markup) first.
                </p>
            );
        }
    } else {
        const html = tab === "productJsonld" ? highlightedProduct : highlightedVideo;
        content = html ? (
            <pre
                className={classNames(PRE_CLS, "whitespace-pre", busy && "opacity-45")}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        ) : null;
    }
    if (!content) {
        let hint: string;
        if (!analyzed) hint = `No ${JSON_LD_TAB_LABELS[tab]} yet. Run a PDP Analysis first — it finds the videos on the page and generates the markup.`;
        else if (isProductTab) {
            if (busy) hint = "Generating the page markup…";
            else if (onGenerateProduct) hint = pageVideoCount > 0 ? "No page JSON-LD yet. Generate it for the videos found on the page." : "The last PDP analysis found no video on this page, so there is no markup to put on it yet.";
            else hint = "No page JSON-LD yet.";
        } else if (busy) hint = onGenerate ? "Generating JSON-LD from the video transcript…" : "Generating markup for the videos on the page…";
        else if (onGenerate) hint = `No ${JSON_LD_TAB_LABELS[tab]} yet. Generate it from the linked video.`;
        else if (pageVideoCount > 0) hint = "No Video Object JSON-LD yet. Generate it for the videos found on the page.";
        else hint = "The last PDP analysis found no video on this page, so there is no VideoObject to publish. Run it again after the page gains a video, or assign one of our videos.";
        content = (
            <p
                className={classNames(
                    "text-[13px] leading-[1.5]",
                    busy ? "text-accent-text animate-pulse" : "text-muted"
                )}
            >
                {hint}
            </p>
        );
    }

    const pageVideos = row.page_videos || [];
    // "Missing video file URL" has two different causes once a page has been
    // analyzed: detection ran and did not see our video there, or the page
    // simply has no video. Say which, so the fix is obvious.
    const missingFileUrl =
        !!ownValidation &&
        !ownValidation.eligible &&
        ownValidation.errors.some((e) => e.field === "contentUrl");
    const oursOnPage = !!ownLink && pageVideos.some((v) => linkForPageVideo(row, v)?.s3_file_id === ownLink.s3_file_id);
    let notFoundNote: string | null = null;
    if (missingFileUrl && analyzed && !oursOnPage) {
        notFoundNote =
            pageVideos.length === 0
                ? "The last PDP analysis found no video on this page, so this video could not be matched. If the page does show it, enter its file URL by hand."
                : `The last PDP analysis found ${pageVideos.length === 1 ? "1 video" : `${pageVideos.length} videos`} on this page, none matching this video's duration. If one of them is it, enter its file URL by hand.`;
    }
    // With several of ours on the page, say which is which.
    const ownLabel = (link: PdpUrlVideoLink) => {
        if (links.length <= 1) return "ours";
        const name = link.videoFile?.name || link.videoFile?.key || `video ${link.s3_file_id}`;
        return `ours · ${String(name).slice(0, 40)}`;
    };

    return (
        <Box className={classNames("flex flex-col", className)}>
            {ownValidation && (
                <Box className="mb-3 flex flex-col gap-[4px]">
                    <JsonLdEligibility validation={ownValidation} size={eligibilitySize} />
                    {notFoundNote && <span className="text-[11.5px] text-muted">{notFoundNote}</span>}
                </Box>
            )}
            {pageVideos.length > 0 && (
                // What the last analysis found on the page. Each gets its own
                // VideoObject in the Video Object tab; the check is Google's.
                // A tag-only entry (a visitor's browser reported it, no page
                // check has seen it) sits muted at the end, not counted.
                <Box className="mb-3 flex flex-col gap-[4px]">
                    <span className="text-[12px] font-semibold text-text-2">
                        {(() => {
                            const found = pageVideos.filter((v) => !tagOnlyVideo(v)).length;
                            const reported = pageVideos.length - found;
                            let base = `${found} videos found on this page`;
                            if (found === 0) base = "No video confirmed on this page";
                            if (found === 1) base = "1 video found on this page";
                            return reported ? `${base} · ${reported} reported by the tag, not yet confirmed` : base;
                        })()}
                    </span>
                    {pageVideos.slice().sort((a, b) => Number(tagOnlyVideo(a)) - Number(tagOnlyVideo(b))).map((v, i) => {
                        const url = v.contentUrl || v.embedUrl || "";
                        const tagOnly = tagOnlyVideo(v);
                        // Ours by the API's stamp (known file URL, or duration / aspect match).
                        const ours = linkForPageVideo(row, v);
                        let dotCls = "bg-border-token";
                        let dotTitle = "No markup yet";
                        if (tagOnly) {
                            dotCls = "border border-dashed border-border-token bg-transparent";
                            dotTitle = "Reported by the tag from a visitor's browser; no page check has seen it yet. It leaves the list after 14 days unseen.";
                        } else if (v.json_ld_validation && v.json_ld_validation.eligible) {
                            dotCls = "bg-[var(--up)]";
                            dotTitle = "Verified for Google video results";
                        } else if (v.json_ld_validation) {
                            dotCls = "bg-[var(--down)]";
                            dotTitle = `Not eligible: missing ${v.json_ld_validation.errors.map((e) => e.field).join(", ")}`;
                        }
                        return (
                            <span key={url || i} className={classNames("flex items-center gap-[6px] text-[11.5px] min-w-0", tagOnly ? "text-muted opacity-60 italic" : "text-muted")}>
                                <span className={classNames("w-[7px] h-[7px] rounded-full shrink-0", dotCls)} title={dotTitle} />
                                {tagOnly && <span className="shrink-0 not-italic" title={dotTitle}>reported by the tag</span>}
                                {ours && !tagOnly && <span className="text-accent-text font-semibold shrink-0">{ownLabel(ours)}</span>}
                                <a href={url} target="_blank" rel="noreferrer" className="truncate hover:text-accent-text" title={url}>
                                    {url.replace(/^https?:\/\//i, "")}
                                </a>
                                {fmtDuration(v.duration) && <span className="shrink-0 tabular-nums">{fmtDuration(v.duration)}</span>}
                                <span className="shrink-0">{KIND_LABELS[v.kind] || v.kind}</span>
                            </span>
                        );
                    })}
                </Box>
            )}
            <Box className="flex items-center justify-between gap-[10px] flex-wrap mb-3">
                <Box className="inline-flex gap-[3px] bg-surface-2 rounded-[10px] p-1">
                    <button type="button" onClick={() => setTab("videoJsonld")} className={tabCls(tab === "videoJsonld")}>
                        <IconCode width={14} height={14} stroke="currentColor" /> Video Object
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("productJsonld")}
                        className={tabCls(tab === "productJsonld")}
                    >
                        <IconCode width={14} height={14} stroke="currentColor" /> Page markup
                    </button>
                    <button type="button" onClick={() => setTab("metadata")} className={tabCls(tab === "metadata")}>
                        <IconInfo size={14} stroke="currentColor" /> Metadata
                    </button>
                </Box>
                <Box className="flex gap-[6px]">
                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!hasContent[tab]}
                        className="inline-flex items-center gap-[5px] px-3 py-[6px] rounded-lg text-[12px] font-semibold text-text-2 bg-surface border border-border-token hover:border-accent hover:text-text-1 transition-colors disabled:opacity-50"
                    >
                        <IconCopy width={13} height={13} stroke="currentColor" /> Copy
                    </button>
                    {generateAction && tab !== "metadata" && (
                        <button
                            type="button"
                            onClick={generateAction}
                            disabled={busy || !analyzed}
                            title={generateTitle}
                            className="inline-flex items-center gap-[5px] px-3 py-[6px] rounded-lg text-[12px] font-semibold text-accent-text bg-tint border border-[#EAD9B8] dark:border-border-token hover:bg-[#F7EDD9] dark:hover:bg-surface-2 transition-colors disabled:opacity-60"
                        >
                            <IconRefresh size={13} stroke="currentColor" />
                            {generateLabel}
                        </button>
                    )}
                </Box>
            </Box>
            {content}
        </Box>
    );
}
